import { useLayoutEffect, useRef, useState } from 'react';
import { ErreurApi } from '../api/client';
import { useMajNote } from '../api/hooks';
import type { CommandeDetaillee } from '../api/types';
import { dateHeure } from '../format';
import { useToasts } from './Toasts';

/**
 * La note de l'équipe, dans le récapitulatif.
 *
 * Elle ne doit pas prendre de place : deux lignes au plus, le reste se déroule
 * au clic. Vide par défaut, elle se réduit alors à un lien « Ajouter une note ».
 * Chaque version est signée et datée par celui qui l'écrit.
 */
export function NoteCommande({ commande }: { commande: CommandeDetaillee }) {
  const note = commande.noteUtilisateur;
  const majour = useMajNote();
  const { ajouter } = useToasts();

  const [edition, setEdition] = useState(false);
  const [brouillon, setBrouillon] = useState('');
  const [deplie, setDeplie] = useState(false);
  /** Le texte dépasse-t-il les deux lignes ? Sans cela, rien à dérouler. */
  const [tronque, setTronque] = useState(false);
  const texte = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = texte.current;
    if (el && !deplie) setTronque(el.scrollHeight > el.clientHeight + 1);
  }, [note?.texte, deplie, edition]);

  const ouvrir = () => { setBrouillon(note?.texte ?? ''); setEdition(true); };

  async function enregistrer() {
    try {
      await majour.mutateAsync({ id: commande.id, texte: brouillon });
      setEdition(false);
      setDeplie(false);
    } catch (e) {
      ajouter(e instanceof ErreurApi ? e.message : 'La note n’a pas pu être enregistrée.', 'bad');
    }
  }

  if (edition) {
    return (
      <div style={{ marginTop: 10 }}>
        <textarea
          rows={3} value={brouillon} maxLength={2000} autoFocus
          aria-label="Note" placeholder="Note visible de toute l’équipe — vide pour l’effacer"
          onChange={(e) => setBrouillon(e.target.value)}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button type="button" className="btn pri sm" disabled={majour.isPending} onClick={() => void enregistrer()}>
            {majour.isPending ? <><span className="spin" /> …</> : 'Enregistrer'}
          </button>
          <button type="button" className="btn sm" disabled={majour.isPending} onClick={() => setEdition(false)}>
            Annuler
          </button>
        </div>
      </div>
    );
  }

  if (!note) {
    return (
      <button type="button" className="lien-etape" style={{ marginTop: 10, fontSize: 12.5 }} onClick={ouvrir}>
        + Ajouter une note
      </button>
    );
  }

  const basculer = () => { if (tronque || deplie) setDeplie((d) => !d); };

  return (
    <div style={{ marginTop: 10 }}>
      <div
        ref={texte}
        role={tronque || deplie ? 'button' : undefined}
        tabIndex={tronque || deplie ? 0 : undefined}
        aria-expanded={tronque || deplie ? deplie : undefined}
        title={tronque && !deplie ? 'Afficher toute la note' : undefined}
        onClick={basculer}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); basculer(); } }}
        style={{
          fontSize: 12.5,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          cursor: tronque || deplie ? 'pointer' : undefined,
          ...(deplie ? {} : {
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }),
        }}
      >
        {note.texte}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <span>{note.par}, le {dateHeure(note.le)}</span>
        {(tronque || deplie) && (
          <button type="button" className="lien-etape" style={{ fontSize: 11.5 }} onClick={basculer}>
            {deplie ? 'Réduire' : 'Lire la suite'}
          </button>
        )}
        <button type="button" className="lien-etape" style={{ fontSize: 11.5 }} onClick={ouvrir}>
          Modifier
        </button>
      </div>
    </div>
  );
}
